import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, Trash2, Send, Newspaper, MapPin } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function CommunityFeed() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [feeds, setFeeds] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({ title: "", body: "", lga: "", image_path: "" });
  const [preview, setPreview] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/feeds").then((r) => setFeeds(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {}); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/feeds/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", data.image_path);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setPreview(""); }
    finally { setUploading(false); }
  };

  const post = async () => {
    setPosting(true);
    try {
      await api.post("/admin/feeds", { title: f.title, body: f.body, lga: isSuper ? f.lga : undefined, image_path: f.image_path || undefined });
      toast.success("Update posted to the community feed");
      setF({ title: "", body: "", lga: "", image_path: "" }); setPreview(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/feeds/${id}`); toast.success("Post removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const valid = f.title && f.body && (isSuper ? f.lga : true) && !uploading;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl">Community Feed</h1>
        <p className="text-muted-foreground">{isSuper ? "Post ward updates for any LGA." : `Post updates for ${user?.assigned_lga} LGA.`}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* composer */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 h-fit">
          <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><Send className="w-5 h-5 text-primary" /> New Update</h3>
          <div className="space-y-3">
            <div><Label className="mb-1.5 block text-sm font-semibold">Title</Label><Input data-testid="feed-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Keffi Ward Outreach" /></div>
            {isSuper && (
              <div><Label className="mb-1.5 block text-sm font-semibold">LGA</Label>
                <Select value={f.lga} onValueChange={(v) => set("lga", v)}>
                  <SelectTrigger data-testid="feed-lga"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                  <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label className="mb-1.5 block text-sm font-semibold">Update</Label><Textarea data-testid="feed-body" value={f.body} onChange={(e) => set("body", e.target.value)} placeholder="What happened at the ward meeting?" rows={4} /></div>
            <div>
              <Label className="mb-1.5 block text-sm font-semibold">Photo (optional)</Label>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl py-6 cursor-pointer hover:border-primary transition-colors text-sm text-muted-foreground">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                {uploading ? "Uploading..." : "Choose a photo"}
                <input data-testid="feed-image" type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
              {preview && <img src={preview} alt="preview" className="mt-3 rounded-xl w-full aspect-video object-cover" />}
            </div>
            <Button data-testid="feed-post" onClick={post} disabled={!valid || posting} className="w-full rounded-full bg-primary font-bold gap-2 py-6">{posting && <Loader2 className="w-4 h-4 animate-spin" />} Post Update</Button>
          </div>
        </div>

        {/* list */}
        <div className="lg:col-span-3 space-y-4">
          {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
            : feeds.length === 0 ? <div className="bg-white rounded-2xl border border-slate-100 shadow-sm py-16 text-center text-muted-foreground"><Newspaper className="w-8 h-8 mx-auto mb-2 opacity-40" />No updates posted yet.</div>
            : feeds.map((p) => (
              <div key={p.id} data-testid={`feed-item-${p.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {p.image_path && <img src={`${API}/files/${p.image_path}`} alt={p.title} className="w-full aspect-video object-cover" />}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-xs text-primary font-bold"><MapPin className="w-3.5 h-3.5" /> {p.lga} · {(p.created_at || "").slice(0, 10)}</div>
                      <h4 className="font-heading font-bold text-lg mt-1">{p.title}</h4>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button data-testid={`feed-delete-${p.id}`} size="icon" variant="ghost" className="text-accent hover:bg-red-50 shrink-0"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this update?</AlertDialogTitle><AlertDialogDescription>"{p.title}" will be removed from the public feed.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-accent">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.body}</p>
                  <div className="text-xs text-slate-400 mt-3">Posted by {p.author}</div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
